import React, { Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation
} from 'react-router-dom';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import { AuthProvider } from './utils/AuthContext';
import ScrollToTop from './components/ScrollToTop';
import { Toaster } from "react-hot-toast";

// Lazy load secondary routes to keep the initial JS bundle ultra-fast
const MyBookings = lazy(() => import('./pages/MyBookings'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RouteDetailPage = lazy(() => import('./pages/routes/RouteDetailPage'));
const AirportPage = lazy(() => import('./pages/airports/AirportPage'));
const BlogList = lazy(() => import('./pages/blog/BlogList'));
const BlogPost = lazy(() => import('./pages/blog/BlogPost'));

// Lightweight loading fallback for smooth transitions
const PageFallback = () => (
  <div className="flex items-center justify-center min-h-[60vh] bg-black">
    <div className="w-10 h-10 border-2 rounded-full border-taxi-yellow border-t-transparent animate-spin"></div>
  </div>
);

// Layout Controller
const LayoutWrapper = ({ children }) => {
  const { pathname } = useLocation();

  const hideLayoutRoutes = [
    '/login',
    '/my-bookings',
  ];

  const shouldHideLayout = hideLayoutRoutes.some(route =>
    pathname === route || pathname === `${route}/`
  );

  return (
    <div className="flex flex-col min-h-screen">
      {!shouldHideLayout && <Navbar />}
      <div className="flex-grow">{children}</div>
      {!shouldHideLayout && <Footer />}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <Toaster position="top-right" reverseOrder={false} />

        <LayoutWrapper>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* Home */}
              <Route path="/" element={<Home />} />
              <Route path="/home" element={<Home />} />

              {/* Main Pages */}
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/my-bookings" element={<MyBookings />} />
              <Route path="/login" element={<LoginPage />} />

              {/* Blog Pages */}
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:postSlug" element={<BlogPost />} />

              {/* Airport SEO Pages */}
              <Route path="/chennai-airport-taxi" element={<AirportPage />} />
              <Route path="/bangalore-airport-taxi" element={<AirportPage />} />
              <Route path="/coimbatore-airport-taxi" element={<AirportPage />} />
              <Route path="/madurai-airport-taxi" element={<AirportPage />} />

              {/* Location-Specific Route SEO Pages */}
              <Route path="/chennai-to-bangalore-taxi" element={<RouteDetailPage />} />
              <Route path="/chennai-to-trichy-taxi" element={<RouteDetailPage />} />
              <Route path="/chennai-to-coimbatore-taxi" element={<RouteDetailPage />} />
              <Route path="/chennai-to-madurai-taxi" element={<RouteDetailPage />} />
              <Route path="/chennai-to-pondicherry-taxi" element={<RouteDetailPage />} />
              <Route path="/chennai-to-salem-taxi" element={<RouteDetailPage />} />
              <Route path="/chennai-to-vellore-taxi" element={<RouteDetailPage />} />
              <Route path="/madurai-to-chennai-taxi" element={<RouteDetailPage />} />
              <Route path="/coimbatore-to-chennai-taxi" element={<RouteDetailPage />} />
              <Route path="/bangalore-to-chennai-taxi" element={<RouteDetailPage />} />
              <Route path="/one-way-taxi-chennai" element={<RouteDetailPage />} />
              <Route path="/chennai-drop-taxi" element={<RouteDetailPage />} />
              <Route path="/pondicherry-taxi" element={<RouteDetailPage />} />
              <Route path="/tirupati-taxi" element={<RouteDetailPage />} />
              <Route path="/coimbatore-taxi" element={<RouteDetailPage />} />
              <Route path="/:routeSlug" element={<RouteDetailPage />} />
            </Routes>
          </Suspense>
        </LayoutWrapper>

      </Router>
    </AuthProvider>
  );
}

export default App;
